import React, { useEffect, useState } from 'react';
import { Alert, App, Button, Card, Col, Form, Input, InputNumber, Modal, Row, Select, Space, Switch, Table, Tabs, Tag, Typography } from 'antd';
import { EditOutlined, PlusOutlined, SaveOutlined, UndoOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';
import RecruitPeoplePicker from './shared/RecruitPeoplePicker';
import { OWNER_ROLE_LABEL, recruitErrorMessage, useRecruitAccess } from './shared/recruitUi';

const { Paragraph, Text } = Typography;
const ROUTED_ROLES = ['HR', 'IT', 'Admin', 'Finance', 'LineManager'];
const MERGE_FIELDS = [
  'candidate_first_name',
  'candidate_name',
  'company_name',
  'job_title',
  'application_number',
  'interview_date',
  'interview_time',
  'interview_duration',
  'interview_mode',
  'interview_location',
  'assessment_title',
  'assessment_date',
  'offer_number',
  'join_date',
  'action_url',
];

/** Recruitment setup: careers site, offer & onboarding defaults, and the reason / email masters (popups). */
const CareersSettingsPage: React.FC = () => {
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();
  const { canManage } = useRecruitAccess();
  const [sp, setSp] = useSearchParams();
  const tab = sp.get('tab') || 'careers';
  const [form] = Form.useForm();
  const [reasonForm] = Form.useForm();
  const [tplForm] = Form.useForm();
  const [reasonModal, setReasonModal] = useState<{ open: boolean; row?: any }>({ open: false });
  const [tplModal, setTplModal] = useState<any>(null);

  const { data, isLoading } = useQuery({ queryKey: hrmsKeys.careersSettings(), queryFn: () => hrmsService.getCareersSettings() });
  const { data: reasons = [] } = useQuery({
    queryKey: hrmsKeys.rejectionReasons('all-admin'),
    queryFn: () => hrmsService.listRejectionReasons({ include_inactive: true }),
    enabled: tab === 'reasons',
  });
  const { data: templates = [] } = useQuery({
    queryKey: hrmsKeys.recruitEmailTemplates(),
    queryFn: () => hrmsService.listRecruitEmailTemplates(),
    enabled: tab === 'emails',
  });

  useEffect(() => {
    if (!data) return;
    const map = (data.onboarding_owner_map_json || {}) as Record<string, string>;
    form.setFieldsValue({ ...data, owner_map: map });
  }, [data, form]);

  const save = useMutation({
    mutationFn: (body: object) => hrmsService.updateCareersSettings(body),
    onSuccess: async () => {
      message.success('Settings saved');
      await queryClient.invalidateQueries({ queryKey: hrmsKeys.careersSettings() });
    },
    onError: (e: any) => message.error(recruitErrorMessage(e, 'Save failed')),
  });

  const submitSettings = async () => {
    const v = await form.validateFields();
    const map = Object.fromEntries(Object.entries(v.owner_map || {}).filter(([, id]) => Boolean(id)));
    save.mutate({ ...v, owner_map: undefined, onboarding_owner_map_json: map });
  };

  const saveReason = async () => {
    const v = await reasonForm.validateFields();
    try {
      if (reasonModal.row) await hrmsService.updateRejectionReason(reasonModal.row.id, v);
      else await hrmsService.createRejectionReason(v);
      message.success('Reason saved');
      setReasonModal({ open: false });
      await queryClient.invalidateQueries({ queryKey: ['hrms', 'rejection-reasons'] });
    } catch (e: any) {
      message.error(recruitErrorMessage(e));
    }
  };

  const saveTemplate = async () => {
    const v = await tplForm.validateFields();
    try {
      await hrmsService.updateRecruitEmailTemplate(tplModal.id, v);
      message.success('Email template saved');
      setTplModal(null);
      await queryClient.invalidateQueries({ queryKey: hrmsKeys.recruitEmailTemplates() });
    } catch (e: any) {
      message.error(recruitErrorMessage(e));
    }
  };

  const resetTemplate = (row: any) =>
    modal.confirm({
      title: `Reset "${row.name}" to the default wording?`,
      onOk: async () => {
        try {
          await hrmsService.resetRecruitEmailTemplate(row.id);
          message.success('Template reset');
          await queryClient.invalidateQueries({ queryKey: hrmsKeys.recruitEmailTemplates() });
        } catch (e: any) {
          message.error(recruitErrorMessage(e));
        }
      },
    });

  const runRetention = () =>
    modal.confirm({
      title: 'Anonymise expired candidate data now?',
      content: 'Candidates whose every application was rejected or withdrawn longer ago than the retention period are redacted and their CVs deleted. This cannot be undone. It also runs automatically once a week.',
      okText: 'Run now',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const r = await hrmsService.runRecruitRetention();
          message.success(`${r?.anonymized ?? 0} candidate(s) anonymised`);
        } catch (e: any) {
          message.error(recruitErrorMessage(e));
        }
      },
    });

  const slug = Form.useWatch('careers_slug', form) || data?.careers_slug;
  const careersUrl = slug ? `${window.location.origin}/careers/${slug}` : null;
  const settingsTab = tab === 'careers' || tab === 'offers';

  return (
    <WorkspaceLayout
      fullBleed
      header={
        <EnterprisePageHeader
          documentLabel="Recruitment setup"
          subtitle="Careers site, offer and onboarding defaults, rejection reasons and candidate emails"
          primaryActions={
            settingsTab && canManage ? (
              <Button type="primary" icon={<SaveOutlined />} loading={save.isPending} onClick={submitSettings}>
                Save
              </Button>
            ) : undefined
          }
        />
      }
    >
      <Form form={form} layout="vertical" disabled={!canManage}>
      <Tabs
        activeKey={tab}
        onChange={(k) => setSp({ tab: k }, { replace: true })}
        items={[
          {
            key: 'careers',
            label: 'Careers site',
            forceRender: true,
            children: (
              <Card size="small" loading={isLoading} style={{ maxWidth: 760 }}>
                <>
                  <Form.Item name="careers_slug" label="Public careers slug" rules={[{ required: true }, { pattern: /^[a-z0-9-]+$/, message: 'Lowercase letters, numbers and hyphens only' }]}>
                    <Input placeholder="acme-corp" />
                  </Form.Item>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item name="brand_title" label="Brand title">
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name="brand_tagline" label="Tagline">
                        <Input />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Form.Item name="consent_text" label="Data-protection consent text (shown on apply)" rules={[{ required: true }]}>
                    <Input.TextArea rows={4} />
                  </Form.Item>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item name="retention_days_rejected" label="Keep rejected candidates for (days)" extra="After this, personal data and CVs are anonymised by the weekly retention job.">
                        <InputNumber min={30} max={3650} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item label=" ">
                        <Button danger onClick={runRetention} disabled={!canManage}>
                          Run anonymisation now
                        </Button>
                      </Form.Item>
                    </Col>
                  </Row>
                  <Form.Item name="require_approved_requisition" label="Require an approved requisition to publish a job" valuePropName="checked">
                    <Switch />
                  </Form.Item>
                  <Form.Item name="is_active" label="Careers site active" valuePropName="checked">
                    <Switch />
                  </Form.Item>
                </>
                {careersUrl && (
                  <Paragraph copyable={{ text: careersUrl }}>
                    Public URL: <Text code>{careersUrl}</Text>
                  </Paragraph>
                )}
                <Paragraph type="secondary">Link to this page from your website, or embed a job's apply page in an iframe.</Paragraph>
              </Card>
            ),
          },
          {
            key: 'offers',
            label: 'Offers and onboarding',
            forceRender: true,
            children: (
              <Card size="small" loading={isLoading} style={{ maxWidth: 760 }}>
                <>
                  <Form.Item name="offer_validity_days" label="Offer valid for (days)" extra="Unanswered offers expire automatically after this many days.">
                    <InputNumber min={1} max={90} style={{ width: 200 }} />
                  </Form.Item>
                  <Text strong>Onboarding task owners</Text>
                  <Paragraph type="secondary">
                    Tasks for each role are assigned to this person when a plan starts. Line-manager tasks go to the new hire's manager; this is the fallback when no manager is set.
                  </Paragraph>
                  {ROUTED_ROLES.map((r) => (
                    <Form.Item key={r} name={['owner_map', r]} label={OWNER_ROLE_LABEL[r]}>
                      <RecruitPeoplePicker placeholder={`Who handles ${OWNER_ROLE_LABEL[r]} tasks`} />
                    </Form.Item>
                  ))}
                </>
              </Card>
            ),
          },
          {
            key: 'reasons',
            label: 'Rejection reasons',
            children: (
              <Card
                size="small"
                style={{ maxWidth: 900 }}
                extra={
                  canManage && (
                    <Button
                      icon={<PlusOutlined />}
                      onClick={() => {
                        reasonForm.resetFields();
                        reasonForm.setFieldsValue({ applies_to: 'All', is_active: true, sort_order: 100 });
                        setReasonModal({ open: true });
                      }}
                    >
                      Add reason
                    </Button>
                  )
                }
              >
                <Table
                  rowKey="id"
                  size="small"
                  pagination={false}
                  dataSource={reasons}
                  columns={[
                    { title: 'Code', dataIndex: 'code', width: 160 },
                    { title: 'Reason', dataIndex: 'name' },
                    { title: 'Used for', dataIndex: 'applies_to', width: 130 },
                    { title: 'Active', dataIndex: 'is_active', width: 80, render: (v) => (v ? <Tag color="green">Yes</Tag> : <Tag>No</Tag>) },
                    ...(canManage
                      ? [
                          {
                            title: '',
                            key: 'e',
                            width: 70,
                            render: (_: unknown, r: any) => (
                              <Button
                                type="link"
                                size="small"
                                icon={<EditOutlined />}
                                onClick={() => {
                                  reasonForm.setFieldsValue(r);
                                  setReasonModal({ open: true, row: r });
                                }}
                              />
                            ),
                          },
                        ]
                      : []),
                  ]}
                />
              </Card>
            ),
          },
          {
            key: 'emails',
            label: 'Candidate emails',
            children: (
              <Card size="small" style={{ maxWidth: 900 }}>
                <Table
                  rowKey="id"
                  size="small"
                  pagination={false}
                  dataSource={templates}
                  columns={[
                    { title: 'Email', dataIndex: 'name' },
                    { title: 'Subject', dataIndex: 'subject', ellipsis: true },
                    { title: 'Active', dataIndex: 'is_active', width: 80, render: (v) => (v ? <Tag color="green">Yes</Tag> : <Tag>Off</Tag>) },
                    ...(canManage
                      ? [
                          {
                            title: '',
                            key: 'e',
                            width: 110,
                            render: (_: unknown, r: any) => (
                              <Space size={0}>
                                <Button
                                  type="link"
                                  size="small"
                                  icon={<EditOutlined />}
                                  onClick={() => {
                                    tplForm.setFieldsValue(r);
                                    setTplModal(r);
                                  }}
                                />
                                <Button type="link" size="small" icon={<UndoOutlined />} title="Reset to default" onClick={() => resetTemplate(r)} />
                              </Space>
                            ),
                          },
                        ]
                      : []),
                  ]}
                />
              </Card>
            ),
          },
        ]}
      />
      </Form>

      <Modal open={reasonModal.open} title={reasonModal.row ? 'Edit reason' : 'New reason'} onOk={saveReason} onCancel={() => setReasonModal({ open: false })} destroyOnClose>
        <Form form={reasonForm} layout="vertical" preserve={false}>
          <Form.Item name="code" label="Code" rules={[{ required: true, pattern: /^[A-Z0-9_]+$/, message: 'Upper-case letters, numbers and _' }]}>
            <Input disabled={Boolean(reasonModal.row)} placeholder="NO_WORK_PERMIT" />
          </Form.Item>
          <Form.Item name="name" label="Reason" rules={[{ required: true, whitespace: true }]}>
            <Input />
          </Form.Item>
          <Row gutter={12}>
            <Col span={10}>
              <Form.Item name="applies_to" label="Used for">
                <Select options={['All', 'Application', 'Requisition', 'Offer'].map((v) => ({ value: v, label: v }))} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="sort_order" label="Order">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="is_active" label="Active" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      <Modal open={Boolean(tplModal)} title={tplModal?.name} width={760} onOk={saveTemplate} onCancel={() => setTplModal(null)} destroyOnClose>
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="Merge fields"
          description={
            <Space size={[4, 4]} wrap>
              {MERGE_FIELDS.map((f) => (
                <Tag key={f}>{`{{${f}}}`}</Tag>
              ))}
            </Space>
          }
        />
        <Form form={tplForm} layout="vertical" preserve={false}>
          <Form.Item name="subject" label="Subject" rules={[{ required: true, whitespace: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="body" label="Body" rules={[{ required: true, whitespace: true }]}>
            <Input.TextArea rows={12} />
          </Form.Item>
          <Form.Item name="is_active" label="Send this email" valuePropName="checked" extra="When off, the email is skipped and logged as such.">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </WorkspaceLayout>
  );
};

export default CareersSettingsPage;
